import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { Building2, FolderGit2, ShieldCheck, ShieldOff, Users2 } from "lucide-react";
import { Card, CardContent } from "../../../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../ui/select";
import EmptyStateCard from "../../EmptyStateCard";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import { saasApi } from "../shared/saasApi";
import { ResourceFilter, ResourceType, SaasResource } from "../shared/saasTypes";
import { formatDate, formatNumber, formatRelative, humanize, PAGE_SIZE, PROVIDERS } from "../shared/saasFormat";
import { KeyValue, ListSkeleton, ListToolbar, MetaTag, ToolbarSearch } from "../shared/SaasUi";
import { WorkspaceContext } from "./workspaceContext";

const TYPE_ICONS: Record<ResourceType, typeof FolderGit2> = {
    REPOSITORY: FolderGit2,
    PROJECT: FolderGit2,
    TEAM: Users2,
    GROUP: Users2,
    ORGANIZATION: Building2,
};

export default function ResourcesTab({ context }: { context: WorkspaceContext }) {
    const meta = PROVIDERS[context.provider];
    const types: { value: ResourceType; label: string }[] = context.provider === "github"
        ? [{ value: "REPOSITORY", label: "Repositories" }, { value: "TEAM", label: "Teams" }, { value: "ORGANIZATION", label: "Organizations" }]
        : [{ value: "PROJECT", label: "Projects" }, { value: "GROUP", label: "Groups" }];
    const [resourceType, setResourceType] = useState<ResourceType>(types[0].value);
    const [searchInput, setSearchInput] = useState("");
    const search = useDebouncedValue(searchInput, 350) as string;
    const [view, setView] = useState<"active" | "all" | "public" | "unprotected" | "archived">("active");
    const [sort, setSort] = useState<NonNullable<ResourceFilter["sort"]>>("default");
    const [page, setPage] = useState(1);

    useEffect(() => setPage(1), [resourceType, search, view, sort, context.connectionId]);

    const codeResource = resourceType === "REPOSITORY" || resourceType === "PROJECT";
    const filter: ResourceFilter = {
        resourceType,
        search: search || undefined,
        visibility: view === "public" ? "public" : undefined,
        unprotected: codeResource && view === "unprotected" ? true : undefined,
        archived: !codeResource ? undefined : view === "archived" ? true : view === "all" ? undefined : false,
        sort,
    };
    const query = useQuery(
        ["saas-resources", context.provider, context.tenantName, context.connectionId, context.range, filter, page],
        () => saasApi.resources(context.provider, context.tenantName, context.connectionId, context.range, filter, page - 1, PAGE_SIZE),
        { keepPreviousData: true },
    );
    const result = query.data;

    return (
        <div className="space-y-2">
            <ListToolbar page={page} totalPages={result?.totalPages ?? 1} onPageChange={setPage} total={result?.totalItems ?? 0} pageSize={PAGE_SIZE}
                         label="Resource pages">
                <ToolbarSearch value={searchInput} onChange={setSearchInput} placeholder={`Search ${meta.resourceLabel.toLowerCase()}...`} />
                <Select value={resourceType} onValueChange={(value) => { setResourceType(value as ResourceType); setView("active"); }}>
                    <SelectTrigger className="saas-control w-44" aria-label="Resource type">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {types.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                                {type.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {(codeResource || resourceType === "GROUP") && (
                    <Select value={view} onValueChange={(value) => setView(value as typeof view)}>
                        <SelectTrigger className="saas-control w-52" aria-label="Resource filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="active">{codeResource ? "Active" : "All"}</SelectItem>
                            <SelectItem value="public">Public</SelectItem>
                            {codeResource && <SelectItem value="unprotected">Unprotected default branch</SelectItem>}
                            {codeResource && <SelectItem value="archived">Archived</SelectItem>}
                            {codeResource && <SelectItem value="all">Including archived</SelectItem>}
                        </SelectContent>
                    </Select>
                )}
                <Select value={sort} onValueChange={(value) => setSort(value as NonNullable<ResourceFilter["sort"]>)}>
                    <SelectTrigger className="saas-control w-52" aria-label="Sort resources">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="default">Name</SelectItem>
                        <SelectItem value="members">Most privileged members</SelectItem>
                        <SelectItem value="lastActivity">Recently active</SelectItem>
                    </SelectContent>
                </Select>
            </ListToolbar>

            {query.isLoading ? (
                <ListSkeleton />
            ) : !result || result.items.length === 0 ? (
                <EmptyStateCard icon={FolderGit2} title={`No ${meta.resourceLabel.toLowerCase()} found`} description="Adjust the filters or wait for the first sync to finish."
                                className="cloud-card-surface border-dashed rounded-2xl" />
            ) : (
                result.items.map((resource) => (
                    <ResourceCard key={`${resource.connectionId}-${resource.id}`} resource={resource}
                                  onOpen={() => context.openResource(resource.connectionId, resource.externalId)} />
                ))
            )}
        </div>
    );
}

function ResourceCard({ resource, onOpen }: { resource: SaasResource; onOpen: () => void }) {
    const Icon = TYPE_ICONS[resource.resourceType] ?? FolderGit2;
    const codeResource = resource.resourceType === "REPOSITORY" || resource.resourceType === "PROJECT";
    const language = typeof resource.attributes?.language === "string" ? resource.attributes.language : null;
    return (
        <Card className="cloud-list-card cursor-pointer rounded-2xl" onClick={onOpen} data-testid={`card-saas-resource-${resource.id}`}>
            <CardContent className="p-4">
                <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1.5">
                            <h3 className="font-semibold text-sm truncate" title={resource.fullName}>{resource.fullName}</h3>
                            {resource.visibility && (
                                <MetaTag className={resource.visibility === "public" ? "!text-red-700 dark:!text-red-300" : undefined}>
                                    {humanize(resource.visibility)}
                                </MetaTag>
                            )}
                            {resource.archived && <MetaTag>Archived</MetaTag>}
                            {resource.fork && <MetaTag>Fork</MetaTag>}
                            {language && <MetaTag>{language}</MetaTag>}
                            {codeResource && resource.defaultBranch && (
                                resource.defaultBranchProtected === false ? (
                                    <MetaTag className="!text-amber-700 dark:!text-amber-300">
                                        <ShieldOff className="w-3 h-3 mr-1" />
                                        {resource.defaultBranch} unprotected
                                    </MetaTag>
                                ) : resource.defaultBranchProtected ? (
                                    <MetaTag className="!text-emerald-700 dark:!text-emerald-300">
                                        <ShieldCheck className="w-3 h-3 mr-1" />
                                        {resource.defaultBranch} protected
                                    </MetaTag>
                                ) : null
                            )}
                        </div>
                        {resource.description && <p className="text-xs text-muted-foreground line-clamp-1">{resource.description}</p>}
                    </div>
                    <div className="flex flex-col items-end gap-0.5 flex-shrink-0 text-right">
                        <span className="text-sm font-semibold">{formatNumber(resource.eventCount)}</span>
                        <span className="text-[11px] text-muted-foreground">events</span>
                        {resource.highRiskEventCount > 0 && (
                            <span className="text-[11px] font-medium text-red-600 dark:text-red-400">{formatNumber(resource.highRiskEventCount)} high risk</span>
                        )}
                    </div>
                </div>
                <div className="mt-3 pt-3 border-t grid grid-cols-2 md:grid-cols-4 gap-4">
                    <KeyValue label="Members">{`${formatNumber(resource.memberCount)} · ${formatNumber(resource.privilegedMemberCount)} privileged`}</KeyValue>
                    <KeyValue label="Last activity">{formatRelative(resource.lastActivityAt, "No activity recorded")}</KeyValue>
                    <KeyValue label="Parent">{resource.parentFullName ?? "—"}</KeyValue>
                    <KeyValue label="Created">{formatDate(resource.platformCreatedAt)}</KeyValue>
                </div>
            </CardContent>
        </Card>
    );
}
